import { useState, useRef, useEffect } from 'react';
import api from '../../../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { AgentSparkleAvatar } from '../../ai_agent/v2/AgentSparkleAvatar';
import { Send, Sparkles } from 'lucide-react';

const SUGGESTIONS = [
  'How do I create a managed-service Enterprise plan?',
  'What is the difference between a global and a custom plan?',
  'Why can’t I delete a plan?',
  'How do annual plans and renewals work?',
];

export default function AstridPricingDrawer({ open, onOpenChange }) {
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    setMessages((m) => [...m, { role: 'user', text: question }]);
    setInput('');
    setLoading(true);
    try {
      const { data } = await api.post('/super-admin/subscription/astrid-help', {
        question, page_context: 'SuperAdmin → Subscription Pricing console',
      });
      setMessages((m) => [...m, { role: 'astrid', text: data?.answer || '…', usedLogs: data?.used_logs }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'astrid', text: 'I hit an error reaching my service. Please try again or contact your platform admin.' }]);
    } finally { setLoading(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0" data-testid="astrid-pricing-drawer">
        <SheetHeader className="px-4 py-3 border-b border-slate-200">
          <SheetTitle className="flex items-center gap-2.5">
            <AgentSparkleAvatar name="Astrid" size={30} />
            <div>
              <div style={{ fontFamily: 'Chivo, sans-serif' }} className="font-black tracking-tight leading-none">Ask Astrid</div>
              <div className="text-[11px] font-normal text-slate-400 mt-0.5">Pricing setup helper · read-only</div>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="astrid-messages">
          {messages.length === 0 && (
            <div className="text-sm text-slate-500">
              <p className="mb-3">Hi! I can explain how to set up pricing plans and suggest a configuration for your scenario. I can’t change anything myself — I’ll point you to the right tab.</p>
              <div className="space-y-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} onClick={() => ask(s)} data-testid={`astrid-suggestion-${i}`}
                    className="w-full text-left text-xs rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50 transition-colors flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-violet-500 shrink-0" />{s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-[hsl(221,100%,33%)] text-white' : 'bg-slate-100 text-slate-800'}`} data-testid={`astrid-msg-${m.role}-${i}`}>
                {m.text}
                {m.usedLogs && <div className="mt-1.5 text-[10px] opacity-60">🔍 checked recent logs</div>}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start" data-testid="astrid-shimmer">
              <div className="max-w-[85%] rounded-2xl bg-slate-100 px-3.5 py-3 w-48">
                <div className="space-y-2">
                  <div className="h-2.5 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-[shimmer_1.4s_infinite]" />
                  <div className="h-2.5 w-3/4 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-[shimmer_1.4s_infinite]" />
                  <div className="h-2.5 w-1/2 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-[shimmer_1.4s_infinite]" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 p-3 flex items-center gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && ask()} placeholder="Ask about pricing setup…"
            className="rounded-full" data-testid="astrid-input" disabled={loading} />
          <Button size="icon" className="rounded-full shrink-0" onClick={() => ask()} disabled={loading || !input.trim()} data-testid="astrid-send">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
